import { environments } from '../__settings__/environments';
import type { EnvironmentDescriptor, EnvironmentId } from '../__settings__/environments';
import { getRuntimeConfig } from '../__settings__/runtimeConfig';

const FALLBACK_ENVIRONMENT_ID: EnvironmentId = 'legal';

const toEnvironmentId = (value?: string | null): EnvironmentId => {
  if (!value) {
    return FALLBACK_ENVIRONMENT_ID;
  }

  switch (value.trim().toLowerCase()) {
    case 'legal':
      return 'legal';
    case 'engineering':
    case 'eng':
      return 'engineering';
    default: {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `Unknown REACT_APP_ENVIRONMENT value "${value}"; falling back to ${FALLBACK_ENVIRONMENT_ID}.`,
        );
      }
      return FALLBACK_ENVIRONMENT_ID;
    }
  }
};

const environmentId = toEnvironmentId(
  process.env.REACT_APP_ENVIRONMENT ?? process.env.REACT_APP_TEAM ?? null,
);

export const currentEnvironmentId: EnvironmentId = environmentId;

export const currentEnvironment: EnvironmentDescriptor = environments[environmentId];

export const isLegalEnvironment = currentEnvironmentId === 'legal';

export const isEngineeringEnvironment = currentEnvironmentId === 'engineering';

export type FileSystemType = 'SHAREPOINT' | 'ONEDRIVE';

const resolveFileSystemType = (value?: string | null): FileSystemType => {
  if (!value) {
    return 'SHAREPOINT';
  }

  return value.toUpperCase() === 'ONEDRIVE' ? 'ONEDRIVE' : 'SHAREPOINT';
};

export const getFileSystemType = (): FileSystemType =>
  resolveFileSystemType(process.env.REACT_APP_FILE_SYSTEM ?? null);

export interface SharePointConfig {
  readonly siteId: string;
  readonly driveId: string;
}

export const getSharePointConfig = (): SharePointConfig => ({
  siteId: process.env.REACT_APP_SHAREPOINT_SITE_ID ?? '',
  driveId: process.env.REACT_APP_SHAREPOINT_SITE_DRIVE_ID ?? '',
});

export const getEnvironmentById = (
  id: EnvironmentId,
): EnvironmentDescriptor => environments[id];

/**
 * Feature check — runtime config first, baked descriptor as fallback (P1).
 *
 * Per-KEY fallback: only a flag the fetched config explicitly carries overrides
 * the descriptor, so a tenant whose `features` is `{}` behaves exactly as the
 * build did before runtime config existed. See __settings__/runtimeConfig.ts.
 */
export const isFeatureEnabled = (flag: string): boolean => {
  const runtime = getRuntimeConfig();
  if (runtime && Object.prototype.hasOwnProperty.call(runtime.features, flag)) {
    return Boolean(runtime.features[flag]);
  }
  return Boolean(currentEnvironment.featureFlags[flag]);
};

export const isComponentVisible = (componentKey: string): boolean =>
  Boolean(currentEnvironment.componentVisibility[componentKey]);

/**
 * The engineering vertical's project-type code (P3, domain-model-transition).
 *
 * The backend's /api/matters resource takes the type as a parameter and its registry
 * (matter_types) owns the vocabulary per tenant; backend code carries no client's type
 * literal (the P3 grep-gate). On the frontend the code resolves runtime-config-first —
 * a string-valued `projectTypeCode` entry in the tenant's features bag — with a baked
 * default so the deployed engineering build needs no config row on day one. That baked
 * default is the ONE remaining client literal in the frontend, here as removable
 * config, not in any route or component; a tenant whose registry uses a different code
 * sets features.projectTypeCode in the tenants registry.
 */
export const getProjectTypeCode = (): string => {
  const runtime = getRuntimeConfig();
  const configured = runtime?.features?.projectTypeCode;
  if (typeof configured === 'string' && configured.trim()) {
    return configured.trim();
  }
  return 'ncalpro';
};

export const getTranslationNamespaces = (): readonly string[] =>
  currentEnvironment.translationNamespaces;